import React, { useRef, useEffect, useCallback } from 'react';

/** HTML for one green content box — typing stays inside this single box. */
export const EMAIL_CONTENT_BOX_HTML =
  '<div class="body-block bop-email-text-box" data-bop-email-box="content" style="background:#f8fafc;padding:18px 20px;border-radius:8px;margin:16px 0;border-left:4px solid #40c191;font-size:16px;line-height:1.6;color:#334155"><p><br></p></div>';

function normalizePlainTextLineBreaks(html) {
  return String(html || '').replace(/\r\n?|\n/g, '<br>');
}

function stripEditorArtifacts(html) {
  return String(html || '')
    .replace(/\s*contenteditable="(true|false)"/gi, '')
    .replace(/\s*data-bop-email-box="[^"]*"/gi, '')
    .replace(/\s*data-bop-locked="[^"]*"/gi, '')
    .replace(/\sclass="([^"]*)\bbop-email-text-box\b([^"]*)"/gi, (_, a, b) => {
      const merged = `${a} ${b}`.replace(/\s+/g, ' ').trim();
      return merged ? ` class="${merged}"` : '';
    })
    .replace(/\sclass="([^"]*)\bbop-email-locked\b([^"]*)"/gi, (_, a, b) => {
      const merged = `${a} ${b}`.replace(/\s+/g, ' ').trim();
      return merged ? ` class="${merged}"` : '';
    });
}

/** Inline styles for .body-block — ensures green box survives email clients that strip <style> tags. */
const BODY_BLOCK_INLINE_STYLE =
  'background:#f8fafc;padding:18px 20px;border-radius:8px;margin:16px 0;border-left:4px solid #40c191;font-size:16px;line-height:1.6;color:#334155';

/** Ensure every .body-block div has inline styles for email client compatibility. */
function ensureBodyBlockInlineStyles(root) {
  if (!root) return;
  root.querySelectorAll('.body-block').forEach((el) => {
    el.style.cssText = BODY_BLOCK_INLINE_STYLE;
  });
}

/** Flatten widget boxes to clean HTML for save / email send. */
export function exportEmailEditorHtml(rootOrHtml) {
  if (typeof rootOrHtml === 'string') {
    const doc = new DOMParser().parseFromString(`<div id="wrap">${rootOrHtml}</div>`, 'text/html');
    const wrap = doc.getElementById('wrap');
    if (!wrap) return stripEditorArtifacts(rootOrHtml);
    exportEmailEditorHtml(wrap);
    return stripEditorArtifacts(wrap.innerHTML);
  }

  const root = rootOrHtml;
  if (!root) return '';

  root.querySelectorAll('[data-bop-email-box]').forEach((box) => {
    const raw = box.innerHTML.trim() || '<p><br></p>';
    box.removeAttribute('data-bop-email-box');
    box.classList.remove('bop-email-text-box');
    box.innerHTML = raw;
  });

  mergeAdjacentBodyBlocks(root);
  ensureBodyBlockInlineStyles(root);
  return stripEditorArtifacts(root.innerHTML);
}

/** Merge accidental splits so one box stays one box. */
export function mergeAdjacentBodyBlocks(root) {
  if (!root) return;
  let el = root.firstElementChild;
  while (el) {
    if (el.classList?.contains('body-block') && !el.hasAttribute('data-bop-locked')) {
      let next = el.nextElementSibling;
      while (next?.classList?.contains('body-block') && !next.hasAttribute('data-bop-locked')) {
        const chunk = next.innerHTML.trim();
        if (chunk) {
          const spacer = el.lastElementChild?.tagName === 'P' ? '</p><p>' : '<br>';
          el.insertAdjacentHTML('beforeend', spacer + chunk);
        }
        const remove = next;
        next = next.nextElementSibling;
        remove.remove();
      }
    }
    el = el.nextElementSibling;
  }
}

/** Prepare saved HTML for editing (lock system message box, open user boxes). */
export function prepareEmailEditorHtml(html) {
  let s = String(html || '');

  if (s.includes('data-bop-email-box') || s.includes('bop-email-text-box')) {
    return s;
  }

  s = s.replace(
    /<div class="body-block"(?:\s+style="[^"]*")?>(\s*(?:<span[^>]*>)?\s*\{\{bodyHtml\}\}\s*(?:<\/span>)?\s*)<\/div>/gi,
    '<div class="body-block bop-email-locked" contenteditable="false" data-bop-locked="1">$1</div>'
  );

  s = s.replace(/<div class="body-block"(?:\s+style="[^"]*")?>((?:(?!<div class="body-block").)*?)<\/div>/gi, (match, inner) => {
    if (/\{\{bodyHtml\}\}/.test(inner)) return match;
    const trimmed = inner.trim() || '<p><br></p>';
    return `<div class="body-block bop-email-text-box" data-bop-email-box="content">${trimmed}</div>`;
  });

  return s;
}

const RichTextEditor = ({
  value,
  onChange,
  placeholder = 'Start typing...',
  insertablePlaceholders,
  insertableBlocks,
  emailBoxMode = false,
  contentMinHeightClass = 'min-h-[400px]',
  contentWhiteSpace = 'pre-wrap',
  paragraphMode = false
}) => {
  const editorRef = useRef(null);
  const lastEmittedRef = useRef(null);
  const isEditingRef = useRef(false);

  useEffect(() => {
    if (editorRef.current && !editorRef.current.hasAttribute('contenteditable')) {
      editorRef.current.setAttribute('contenteditable', 'true');
    }
  }, []);

  useEffect(() => {
    if (!editorRef.current || isEditingRef.current) return;
    if (value === lastEmittedRef.current) return;

    const next = emailBoxMode ? prepareEmailEditorHtml(value) : value || '';
    if (editorRef.current.innerHTML !== next) {
      editorRef.current.innerHTML = next;
    }
    lastEmittedRef.current = value ?? '';
  }, [value, emailBoxMode]);

  const emitChange = useCallback(() => {
    if (!onChange || !editorRef.current) return;
    const rawHtml = editorRef.current.innerHTML;
    const html =
      !emailBoxMode && contentWhiteSpace === 'pre-wrap'
        ? normalizePlainTextLineBreaks(rawHtml)
        : rawHtml;
    lastEmittedRef.current = html;
    onChange(html);
  }, [onChange, emailBoxMode, contentWhiteSpace]);

  const handleInput = () => {
    if (emailBoxMode && editorRef.current) {
      mergeAdjacentBodyBlocks(editorRef.current);
    }
    emitChange();
  };

  const handleFocus = () => {
    isEditingRef.current = true;
    // Make Enter create <p> blocks instead of the browser-default <div>.
    if (paragraphMode) {
      try {
        document.execCommand('defaultParagraphSeparator', false, 'p');
      } catch {
        // Unsupported browsers keep their default separator.
      }
    }
  };

  const handleBlur = () => {
    isEditingRef.current = false;
  };

  const execCommand = (command, cmdValue = null) => {
    document.execCommand(command, false, cmdValue);
    editorRef.current?.focus();
  };

  const handleAddLink = () => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    const range =
      selection && selection.rangeCount > 0
        ? selection.getRangeAt(0).cloneRange()
        : null;

    const rawUrl = window.prompt("Enter the URL:", "https://");
    if (!rawUrl) return;

    const trimmed = rawUrl.trim();
    const hasProtocol = /^(https?:\/\/|mailto:|tel:)/i.test(trimmed);
    const url = hasProtocol ? trimmed : `https://${trimmed}`;

    editorRef.current.focus();
    if (range) {
      const currentSelection = window.getSelection();
      currentSelection?.removeAllRanges();
      currentSelection?.addRange(range);
    }

    try {
      document.execCommand("createLink", false, url);
    } catch {
      return;
    }
    handleInput();
  };

  const insertPlaceholderToken = (key) => {
    if (!editorRef.current || !key) return;
    editorRef.current.focus();
    const token = `{{${key}}}`;
    const wrapped = `<span class="bop-email-ph inline-block rounded bg-emerald-50 px-1 py-0.5 font-mono text-[11px] text-emerald-900 border border-emerald-200 align-middle" contenteditable="false">${token}</span>`;
    try {
      document.execCommand('insertHTML', false, wrapped);
    } catch {
      document.execCommand('insertText', false, token);
    }
    handleInput();
  };

  const focusLastTextBox = () => {
    if (!editorRef.current) return;
    const boxes = editorRef.current.querySelectorAll('[data-bop-email-box]');
    const box = boxes[boxes.length - 1];
    if (!box) return;
    const p = box.querySelector('p') || box;
    const range = document.createRange();
    range.selectNodeContents(p);
    range.collapse(false);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
    editorRef.current.focus();
  };

  const insertLayoutBlock = (html) => {
    if (!editorRef.current || !html) return;
    editorRef.current.focus();
    try {
      document.execCommand('insertHTML', false, html);
    } catch {
      return;
    }
    handleInput();
    if (html.includes('data-bop-email-box')) {
      focusLastTextBox();
    }
  };

  const insertContentBox = () => {
    insertLayoutBlock(EMAIL_CONTENT_BOX_HTML);
  };

  const handleKeyDown = (e) => {
    if (!emailBoxMode || e.key !== 'Enter') return;
    const box = e.target.closest?.('[data-bop-email-box]');
    if (!box || !editorRef.current?.contains(box)) return;

    e.preventDefault();
    if (e.shiftKey) {
      document.execCommand('insertParagraph', false, null);
    } else {
      document.execCommand('insertLineBreak', false, null);
    }
    handleInput();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    const escaped = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\r\n?/g, '\n');
    // In paragraph mode, blank lines in pasted text (e.g. from Word or email) become separate paragraphs.
    const paragraphs = escaped.split(/\n\s*\n/).map((para) => para.trim()).filter(Boolean);
    const html =
      paragraphMode && paragraphs.length > 1
        ? paragraphs.map((para) => `<p>${para.replace(/\n/g, '<br>')}</p>`).join('')
        : escaped.replace(/\n/g, '<br>');
    try {
      document.execCommand('insertHTML', false, html);
    } catch {
      document.execCommand('insertText', false, text);
    }
    handleInput();
  };

  return (
    <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
      <div className="flex flex-wrap items-center gap-1 px-3 py-2 border-b border-gray-200 bg-gray-50">
        <button type="button" onClick={() => execCommand('undo')} className="p-2 text-gray-600 hover:bg-gray-200 rounded transition-colors" title="Undo">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M3.33334 8.66667C3.33334 10.0812 3.89501 11.4377 4.89521 12.4379C5.89542 13.4381 7.25189 14 8.66668 14C10.0815 14 11.4379 13.4381 12.4381 12.4379C13.4383 11.4377 14 10.0812 14 8.66667C14 7.25189 13.4383 5.89542 12.4381 4.89521C11.4379 3.89501 10.0815 3.33334 8.66668 3.33334H8.00001M8.00001 3.33334L10 5.33334M8.00001 3.33334L6.00001 5.33334" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <button type="button" onClick={() => execCommand('redo')} className="p-2 text-gray-600 hover:bg-gray-200 rounded transition-colors" title="Redo">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M12.6667 8.66667C12.6667 10.0812 12.105 11.4377 11.1048 12.4379C10.1046 13.4381 8.74811 14 7.33332 14C5.91854 14 4.56207 13.4381 3.56186 12.4379C2.56166 11.4377 2 10.0812 2 8.66667C2 7.25189 2.56166 5.89542 3.56186 4.89521C4.56207 3.89501 5.91854 3.33334 7.33332 3.33334H8M8 3.33334L6 5.33334M8 3.33334L10 5.33334" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        <div className="w-px h-6 bg-gray-300 mx-1" />

        {emailBoxMode ? (
          <>
            <button
              type="button"
              onClick={insertContentBox}
              className="px-3 py-1.5 text-xs sm:text-sm font-medium text-emerald-900 bg-emerald-50 border border-emerald-300 rounded hover:bg-emerald-100"
              title="Add one green box — all typing stays inside it"
            >
              + Text box
            </button>
            <div className="w-px h-6 bg-gray-300 mx-1 shrink-0" />
          </>
        ) : null}

        <select onChange={(e) => execCommand('fontSize', e.target.value)} className="px-2 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-400 cursor-pointer" defaultValue="3">
          <option value="1">Small</option>
          <option value="3">Normal text</option>
          <option value="5">Large</option>
          <option value="7">Huge</option>
        </select>

        <select onChange={(e) => execCommand('formatBlock', e.target.value)} className="px-2 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-400 cursor-pointer" defaultValue="">
          <option value="">Align</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
          <option value="p">Paragraph</option>
        </select>

        <div className="w-px h-6 bg-gray-300 mx-1" />

        <button type="button" onClick={() => execCommand('bold')} className="p-2 text-gray-600 hover:bg-gray-200 rounded transition-colors font-bold" title="Bold">B</button>
        <button type="button" onClick={() => execCommand('italic')} className="p-2 text-gray-600 hover:bg-gray-200 rounded transition-colors italic" title="Italic">I</button>
        <button type="button" onClick={() => execCommand('underline')} className="p-2 text-gray-600 hover:bg-gray-200 rounded transition-colors underline" title="Underline">U</button>
        <button type="button" onClick={handleAddLink} className="p-2 text-gray-600 hover:bg-gray-200 rounded transition-colors" title="Add link">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
        </button>

        <div className="w-px h-6 bg-gray-300 mx-1" />

        <button type="button" onClick={() => execCommand('insertUnorderedList')} className="p-2 text-gray-600 hover:bg-gray-200 rounded transition-colors" title="Bullet List">•</button>
        <button type="button" onClick={() => execCommand('insertOrderedList')} className="p-2 text-gray-600 hover:bg-gray-200 rounded transition-colors" title="Numbered List">1.</button>

        {insertableBlocks && insertableBlocks.length > 0 && !emailBoxMode ? (
          <>
            <div className="w-px h-6 bg-gray-300 mx-1 shrink-0" />
            <select
              aria-label="Insert layout block"
              className="px-2 py-1 text-xs sm:text-sm text-gray-700 bg-white border border-gray-300 rounded"
              value=""
              onChange={(e) => {
                const block = insertableBlocks.find((b) => b.key === e.target.value);
                e.target.value = '';
                if (block) insertLayoutBlock(block.html);
              }}
            >
              <option value="">Insert layout block…</option>
              {insertableBlocks.map((b) => (
                <option key={b.key} value={b.key}>{b.label}</option>
              ))}
            </select>
          </>
        ) : null}

        {/* {insertablePlaceholders && insertablePlaceholders.length > 0 ? (
          <>
            <div className="w-px h-6 bg-gray-300 mx-1 shrink-0" />
            <select
              aria-label="Insert data field"
              className="max-w-[10.5rem] sm:max-w-[16rem] px-2 py-1 text-xs sm:text-sm text-gray-700 bg-white border border-gray-300 rounded"
              value=""
              onChange={(e) => {
                const key = e.target.value;
                e.target.value = '';
                if (key) insertPlaceholderToken(key);
              }}
            >
              <option value="">Insert data field…</option>
              {insertablePlaceholders.map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
          </>
        ) : null} */}
      </div>

      <div
        ref={editorRef}
        className={`${contentMinHeightClass} p-4 text-gray-900 text-sm leading-relaxed focus:outline-none`}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onFocus={handleFocus}
        onBlur={handleBlur}
        data-placeholder={placeholder}
        style={{ whiteSpace: contentWhiteSpace, wordWrap: 'break-word' }}
      />

      <style jsx>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
          display: block;
        }
        [contenteditable] { outline: none; }
        [contenteditable] ul, [contenteditable] ol { padding-left: 1.5rem; margin: 0.5rem 0; }
        [contenteditable] li { margin: 0.25rem 0; }
        [contenteditable] a { color: #40c191; text-decoration: underline; }
        [contenteditable] h1 { font-size: 2rem; font-weight: 700; margin: 1rem 0; }
        [contenteditable] h2 { font-size: 1.5rem; font-weight: 600; margin: 0.75rem 0; }
        [contenteditable] h3 { font-size: 1.25rem; font-weight: 600; margin: 0.5rem 0; }
        [contenteditable] p { margin: 0.5rem 0; }
        [contenteditable] .bop-email-ph { user-select: all; }
        [contenteditable] .body-block,
        [contenteditable] .bop-email-text-box {
          background: #f8fafc;
          padding: 14px 16px;
          border-radius: 8px;
          margin: 12px 0;
          border-left: 4px solid #40c191;
        }
        [contenteditable] .bop-email-locked { opacity: 0.92; }
        [contenteditable] [data-bop-email-box] p { margin: 0 0 8px; }
        [contenteditable] [data-bop-email-box] p:last-child { margin-bottom: 0; }
        [contenteditable] .notice {
          background: #fffbeb;
          border: 1px solid #fde68a;
          color: #78350f;
          padding: 12px 14px;
          border-radius: 8px;
          margin: 12px 0;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
